import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Bot, Plus, Search, MessageSquare, Settings, Sparkles, Trash2 } from 'lucide-react';
import { Avatar, AvatarFallback } from './ui/avatar';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog';
import { toast } from 'sonner';

export function AgentsPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [agents, setAgents] = useState([
    {
      id: '1',
      name: 'Asistente de Ventas',
      description: 'Ayuda a redactar propuestas comerciales y responder consultas de clientes',
      provider: 'Claude',
      model: 'claude-3-opus-20240229',
      conversations: 156,
      avgResponseTime: '1.2s',
      systemPrompt: 'Eres un asistente de ventas profesional...',
    },
    {
      id: '2',
      name: 'Analista de Feedback',
      description: 'Analiza y categoriza feedback de clientes para extraer insights',
      provider: 'ChatGPT',
      model: 'gpt-4-turbo-preview',
      conversations: 89,
      avgResponseTime: '2.1s',
      systemPrompt: 'Tu tarea es analizar feedback de clientes...',
    },
    {
      id: '3',
      name: 'Generador de Contenido',
      description: 'Crea contenido para redes sociales y campañas de marketing',
      provider: 'Claude',
      model: 'claude-3-sonnet-20240229',
      conversations: 234,
      avgResponseTime: '1.5s',
      systemPrompt: 'Eres un experto en marketing de contenidos...',
    },
    {
      id: '4',
      name: 'Soporte Técnico',
      description: 'Responde preguntas técnicas y ayuda con troubleshooting',
      provider: 'ChatGPT',
      model: 'gpt-4',
      conversations: 412,
      avgResponseTime: '1.8s',
      systemPrompt: 'Eres un experto en soporte técnico...',
    },
  ]);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [agentToDelete, setAgentToDelete] = useState<string | null>(null);
  const navigate = useNavigate();

  const filteredAgents = agents.filter(a => 
    a.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    a.description.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getProviderColor = (provider: string) => {
    return provider === 'Claude' ? 'bg-orange-500' : 'bg-green-500';
  };

  const handleChatWithAgent = (agentId: string, agentName: string) => {
    toast.success(`Abriendo chat con ${agentName}...`);
    // Aquí normalmente navegarías a la página de chat
    navigate(`/dashboard/agents/${agentId}/chat`);
  };

  const handleDeleteAgent = () => {
    if (agentToDelete) {
      const agent = agents.find(a => a.id === agentToDelete);
      setAgents(prev => prev.filter(a => a.id !== agentToDelete));
      toast.success(`Agente "${agent?.name}" eliminado correctamente`);
      setDeleteDialogOpen(false);
      setAgentToDelete(null);
    }
  };

  const openDeleteDialog = (agentId: string) => {
    setAgentToDelete(agentId);
    setDeleteDialogOpen(true);
  };

  return (
    <div className="space-y-8 p-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl mb-2">AI Agents</h1>
          <p className="text-muted-foreground">
            Configura y gestiona tus asistentes de inteligencia artificial
          </p>
        </div>
        <Button className="bg-primary hover:bg-primary/90" asChild>
          <Link to="/agents/new">
            <Plus className="mr-2 h-4 w-4" />
            Nuevo Agente
          </Link>
        </Button>
      </div>

      {/* Search */}
      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Buscar agentes..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9 h-11"
          />
        </div>
      </div>

      {/* Agents Grid */}
      <div className="grid gap-6 md:grid-cols-2">
        {filteredAgents.map((agent) => (
          <Card key={agent.id} className="relative overflow-hidden border-border hover:shadow-lg transition-shadow">
            {/* Línea azul superior elegante */}
            <div className="absolute top-0 left-0 right-0 h-0.5 bg-primary"></div>
            
            <CardHeader>
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-4">
                  <Avatar className="h-12 w-12">
                    <AvatarFallback className={`${getProviderColor(agent.provider)} text-white`}>
                      <Bot className="h-6 w-6" />
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <CardTitle className="mb-1.5">{agent.name}</CardTitle>
                    <CardDescription>{agent.description}</CardDescription>
                    <div className="flex items-center gap-2 mt-3">
                      <Badge variant="outline" className="text-xs">
                        <Sparkles className="mr-1 h-3 w-3" />
                        {agent.provider}
                      </Badge>
                      <Badge variant="secondary" className="text-xs">
                        {agent.model}
                      </Badge>
                    </div>
                  </div>
                </div>
                <div className="flex gap-1">
                  <Button variant="ghost" size="icon" asChild>
                    <Link to={`/agents/${agent.id}/settings`}>
                      <Settings className="h-4 w-4" />
                    </Link>
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="icon"
                    onClick={() => openDeleteDialog(agent.id)}
                    className="text-destructive hover:text-destructive"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4 text-sm mb-4">
                <div>
                  <p className="text-muted-foreground mb-1 text-xs font-medium">Conversaciones</p>
                  <p className="font-medium">{agent.conversations}</p>
                </div>
                <div>
                  <p className="text-muted-foreground mb-1 text-xs font-medium">Tiempo respuesta</p>
                  <p className="font-medium">{agent.avgResponseTime}</p>
                </div>
              </div>

              <div className="flex gap-2">
                <Button 
                  size="sm" 
                  className="flex-1 bg-primary hover:bg-primary/90"
                  onClick={() => handleChatWithAgent(agent.id, agent.name)}
                >
                  <MessageSquare className="mr-2 h-4 w-4" />
                  Chatear
                </Button>
                <Button size="sm" variant="outline" asChild>
                  <Link to={`/agents/${agent.id}`}>Ver detalles</Link>
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Empty State */}
      {filteredAgents.length === 0 && (
        <Card className="relative overflow-hidden border-border">
          <div className="absolute top-0 left-0 right-0 h-0.5 bg-primary"></div>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-muted mb-4">
              <Bot className="h-8 w-8 text-muted-foreground" />
            </div>
            <p className="text-muted-foreground mb-4">No se encontraron agentes</p>
            <Button variant="outline" asChild>
              <Link to="/agents/new">
                <Plus className="mr-2 h-4 w-4" />
                Crear primer agente
              </Link>
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Delete Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminar Agente</AlertDialogTitle>
            <AlertDialogDescription>
              ¿Estás seguro de que quieres eliminar este agente de IA? 
              Esta acción no se puede deshacer y se perderán todas las conversaciones.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive hover:bg-destructive/90"
              onClick={handleDeleteAgent}
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}